import React from 'react';
import { RouteObject } from 'react-router-dom';

import { suspense } from './utils';

const App = React.lazy(
  () => import('../App/App.tsx') as unknown as Promise<{ default: React.FC }>,
);
const TextTranslation = React.lazy(
  () =>
    import(
      '../pages/Application/TextTranslation/TextTranslation.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const MachineTranslation = React.lazy(
  () =>
    import(
      '../pages/Application/MachineTranslation/MachineTranslation.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const NotFound = React.lazy(
  () =>
    import('../pages/NotFound/NotFound.tsx') as unknown as Promise<{
      default: React.FC;
    }>,
);
const History = React.lazy(
  () =>
    import('../pages/Application/History/History.tsx') as unknown as Promise<{
      default: React.FC;
    }>,
);
const MyProfile = React.lazy(
  () =>
    import(
      '../pages/Administration/UserProfile/UserProfile.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const SpeechToText = React.lazy(
  () =>
    import(
      '../pages/Application/SpeechToText/SpeechToText.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const SpeechHistory = React.lazy(
  () =>
    import(
      '../pages/Application/SpeechHistory/SpeechHistory.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const Help = React.lazy(
  () =>
    import('../pages/Application/Help/Help.tsx') as unknown as Promise<{
      default: React.FC;
    }>,
);
const TranslationGlobalService = React.lazy(
  () =>
    import('../pages/TranslationGlobalService.tsx') as unknown as Promise<{
      default: React.FC;
    }>,
);
const SpeechToTextGlobalService = React.lazy(
  () =>
    import('../pages/SpeechToTextGlobalService.tsx') as unknown as Promise<{
      default: React.FC;
    }>,
);
const DocumentAnalyser = React.lazy(
  () =>
    import(
      '../pages/Application/DocumentAnalyser/LcLlm.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const TMManager = React.lazy(
  () =>
    import(
      '../pages/Application/TMManager/TMManager.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const Glossaries = React.lazy(
  () =>
    import(
      '../pages/Application/Glossaries/Glossaries.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const KnowledgeBase = React.lazy(
  () =>
    import(
      '../pages/Application/KnowledgeBase/KnowledgeBase.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const Company = React.lazy(
  () =>
    import(
      '../pages/Administration/Company/Company.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);
const HelpAdministration = React.lazy(
  () =>
    import(
      '../pages/Administration/HelpAdministration/HelpAdministration.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const AdminTranslation = React.lazy(
  () =>
    import(
      '../pages/Administration/Translation/Translation.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const AdminGlossaries = React.lazy(
  () =>
    import(
      '../pages/Administration/Translation/Pages/AdminGlossaries/AdminGlossaries.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const AdminSuggestions = React.lazy(
  () =>
    import(
      '../pages/Administration/Translation/Pages/AdminSuggestions/AdminSuggestions.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

const AdminKnowledgeBase = React.lazy(
  () =>
    import(
      '../pages/Administration/KnowledgeBase/AdminKnowledgeBase.tsx'
    ) as unknown as Promise<{ default: React.FC }>,
);

export interface AppRoute<P extends unknown[] = never[]> {
  name: string;
  path: (...args: P) => string;
}

function typeRoutes<T extends Record<string, AppRoute>>(
  routes: T,
): { [K in keyof T]: T[K] } {
  return routes;
}

export const routes = typeRoutes({
  TranslationGlobalService: {
    name: '',
    path() {
      return '/';
    },
  },
  MachineTranslation: {
    name: '',
    path() {
      return '/';
    },
  },
  TextTranslation: {
    name: 'TextTranslation',
    path() {
      return '/TextTranslation';
    },
  },
  History: {
    name: 'History',
    path() {
      return '/History';
    },
  },
  TMManager: {
    name: 'TMManager',
    path() {
      return '/TMManager';
    },
  },
  Glossaries: {
    name: 'Glossaries',
    path() {
      return '/Glossaries';
    },
  },
  SpeechToTextGlobalService: {
    name: 'SpeechToText',
    path() {
      return '/SpeechToText';
    },
  },
  SpeechToText: {
    name: '',
    path() {
      return '/SpeechToText';
    },
  },
  SpeechHistory: {
    name: 'History',
    path() {
      return '/SpeechToText/History';
    },
  },
  TableExtractorGlobalService: {
    name: 'TableExtractor',
    path() {
      return '/TableExtractor';
    },
  },
  DocumentAnalyser: {
    name: 'DocumentAnalyser',
    path() {
      return '/DocumentAnalyser';
    },
  },
  DocumentAnalyserTableGenerator: {
    name: 'TableGenerator',
    path() {
      return '/TableGenerator';
    },
  },
  KnowledgeBase: {
    name: 'KnowledgeBase',
    path() {
      return '/KnowledgeBase';
    },
  },
  Help: {
    name: 'Help',
    path() {
      return '/help';
    },
  },
  MyProfile: {
    name: '/Administration/MyProfile',
    path() {
      return '/Administration/MyProfile';
    },
  },
  Company: {
    name: '/Administration/Company',
    path() {
      return '/Administration/Company';
    },
  },
  HelpAdministration: {
    name: '/Administration/Help',
    path() {
      return '/Administration/Help';
    },
  },
  AdminTranslation: {
    name: '/Administration/Translation',
    path() {
      return '/Administration/Translation';
    },
  },
  AdminGlossaries: {
    name: '/Administration/Translation/Glossaries',
    path() {
      return '/Administration/Translation/Glossaries';
    },
  },
  AdminSuggestions: {
    name: '/Administration/Translation/Suggestions',
    path() {
      return '/Administration/Translation/Suggestions';
    },
  },
  AdminKnowledgeBase: {
    name: '/Administration/KnowledgeBase',
    path() {
      return '/Administration/KnowledgeBase';
    },
  },
});

export const routerRoutes: RouteObject[] = [
  {
    element: suspense(App),
    children: [
      {
        path: routes.TranslationGlobalService.name,
        element: suspense(TranslationGlobalService),
        children: [
          {
            path: routes.MachineTranslation.name,
            element: suspense(MachineTranslation),
          },
          {
            path: routes.TextTranslation.name,
            element: suspense(TextTranslation),
          },
          {
            path: routes.History.name,
            element: suspense(History),
          },
          {
            path: routes.TMManager.name,
            element: suspense(TMManager),
          },
          {
            path: routes.Glossaries.name,
            element: suspense(Glossaries),
          },
        ],
      },
      {
        path: routes.SpeechToTextGlobalService.name,
        element: suspense(SpeechToTextGlobalService),
        children: [
          {
            path: routes.SpeechToText.name,
            element: suspense(SpeechToText),
          },
          {
            path: routes.SpeechHistory.name,
            element: suspense(SpeechHistory),
          },
        ],
      },
      {
        path: routes.DocumentAnalyser.name,
        element: suspense(DocumentAnalyser),
      },
      {
        path: routes.KnowledgeBase.name,
        element: suspense(KnowledgeBase),
      },
      {
        path: routes.MyProfile.name,
        element: suspense(MyProfile),
      },
      {
        path: routes.Company.name,
        element: suspense(Company),
      },
      {
        path: routes.Help.name,
        element: suspense(Help),
      },
      {
        path: routes.HelpAdministration.name,
        element: suspense(HelpAdministration),
      },
      {
        path: routes.AdminTranslation.name,
        element: suspense(AdminTranslation),
        children: [
          {
            path: routes.AdminGlossaries.name,
            element: suspense(AdminGlossaries),
          },
          {
            path: routes.AdminSuggestions.name,
            element: suspense(AdminSuggestions),
          },
        ],
      },
      {
        path: routes.AdminKnowledgeBase.name,
        element: suspense(AdminKnowledgeBase),
      },
    ],
  },
  {
    path: '*',
    element: suspense(NotFound),
  },
];
